import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useState, useEffect } from "react";
import { Users, Shield, Store, Radio, MessageSquare, LogOut, Terminal, UserCog, HelpCircle, LayoutDashboard, ShieldCheck, Menu, X, Key } from "lucide-react";
import { Toaster } from "./ui/sonner";
import NotificationBell from "./NotificationBell";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");

  const doLogout = () => { logout(); nav("/login"); };
  const closeOnNav = () => setOpen(false);

  const submitChangePassword = async () => {
    if (!curPwd || !newPwd) { toast.error("Both fields required"); return; }
    try {
      await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd });
      toast.success("Password changed");
      setPwdOpen(false); setCurPwd(""); setNewPwd("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Backend health check — toggles SIGNAL badge
  const [linkOk, setLinkOk] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        await api.get("/", { timeout: 5000 });
        if (!cancelled) setLinkOk(true);
      } catch {
        if (!cancelled) setLinkOk(false);
      }
    };
    check();
    const id = setInterval(check, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <div className="min-h-screen flex bg-[#050505] text-[#EAEAEA] relative">
      <div className="crt-overlay" />

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-3 py-2 border-b border-[rgba(255,176,0,0.25)] bg-[#050505]">
        <button data-testid="sidebar-toggle" onClick={() => setOpen(!open)} className="p-2 amber border border-[rgba(255,176,0,0.4)]">
          {open ? <X size={18}/> : <Menu size={18}/>}
        </button>
        <div className="flex items-center gap-2 amber heading-mono text-sm">
          <Terminal size={16} strokeWidth={1.5} /> SPACEBOOK
        </div>
        <NotificationBell />
      </div>

      {/* Sidebar */}
      <aside data-testid="sidebar" className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 h-screen w-64 border-r border-[rgba(255,176,0,0.25)] flex flex-col bg-[#050505] z-50 transition-transform duration-200`}>
        <div className="p-5 border-b border-[rgba(255,176,0,0.25)] flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 amber amber-glow heading-mono text-xl flicker">
              <Terminal size={20} strokeWidth={1.5} />
              <span>SPACEBOOK</span>
            </div>
            <div className="mono text-[10px] text-[#A0A0A0] mt-1 tracking-[0.2em] uppercase">W-Y // Nostromo OS v1.75</div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden amber p-1"><X size={16}/></button>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="label-term px-4 mb-2">// Navigation</div>
          <NavLink to="/dashboard" onClick={closeOnNav} data-testid="nav-dashboard" className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}>
            <LayoutDashboard size={16} strokeWidth={1.5} /> Dashboard
          </NavLink>
          <NavLink to="/characters" onClick={closeOnNav} data-testid="nav-characters" className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}>
            <Users size={16} strokeWidth={1.5} /> Characters
          </NavLink>
          <NavLink to="/campaigns" onClick={closeOnNav} data-testid="nav-campaigns" className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}>
            <Shield size={16} strokeWidth={1.5} /> Campaigns
          </NavLink>
          <NavLink to="/store" onClick={closeOnNav} data-testid="nav-store" className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}>
            <Store size={16} strokeWidth={1.5} /> Store
          </NavLink>
          <NavLink to="/friends" onClick={closeOnNav} data-testid="nav-friends" className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}>
            <MessageSquare size={16} strokeWidth={1.5} /> Comms
          </NavLink>
          <NavLink to="/help" onClick={closeOnNav} data-testid="nav-help" className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}>
            <HelpCircle size={16} strokeWidth={1.5} /> Help
          </NavLink>
          {user?.role === "admin" && (
            <>
              <div className="label-term px-4 mt-6 mb-2">// Admin</div>
              <NavLink to="/admin" onClick={closeOnNav} data-testid="nav-admin" className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}>
                <ShieldCheck size={16} strokeWidth={1.5} /> Admin Panel
              </NavLink>
            </>
          )}
        <div className="label-term px-4 mt-6 mb-2">// Account</div>
          <button onClick={() => setPwdOpen(true)} data-testid="change-password-btn" className="nav-link mt-2 w-full text-left">
            <Key size={14} strokeWidth={1.5} /> Change Password
          </button>
          <button onClick={doLogout} data-testid="logout-btn" className="nav-link mt-2 w-full text-left">
            <LogOut size={14} strokeWidth={1.5} /> Disconnect
          </button>
        </nav>

        <div className="border-t border-[rgba(255,176,0,0.25)] p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserCog size={14} strokeWidth={1.5} className="amber" />
            <span className="mono text-sm amber" data-testid="current-user">{user?.username}</span>
            {user?.role === "admin" && <span className="text-[9px] mono px-1.5 border border-[#4AF626] neon-green">ADMIN</span>}
          </div>
          <div className="mono text-[10px] text-[#A0A0A0] break-all">{user?.email}</div>
        </div>
      </aside>

      {/* backdrop */}
      {open && <button aria-label="close" onClick={() => setOpen(false)} className="lg:hidden fixed inset-0 bg-black/70 z-40" />}

      <main className="flex-1 overflow-auto relative pt-12 lg:pt-0">
        <div className="hidden lg:flex items-center gap-3 absolute top-0 right-0 p-3 z-30">
          <span
            data-testid="signal-badge"
            className={`mono text-[10px] tracking-[0.2em] opacity-90 flex items-center gap-1 ${linkOk ? "text-[#4AF626]" : "text-[#FF2A2A]"}`}
            title={linkOk ? "Backend reachable" : "Backend unreachable"}
          >
            <Radio size={10} className={linkOk ? "" : "animate-pulse"} /> SIGNAL // {linkOk ? "LINK OK" : "BAD SIGNAL"}
          </span>
          <NotificationBell />
        </div>
        <Outlet />
      </main>

      <Toaster theme="dark" toastOptions={{
        style: { background: "#000", border: "1px solid #FFB000", color: "#FFB000", fontFamily: "Source Code Pro, monospace", borderRadius: 0 }
      }} />

      {pwdOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80" data-testid="change-password-modal">
          <div className="panel p-6 w-full max-w-md mx-4 border border-[#FFB000]">
            <div className="heading-mono amber uppercase mb-1">// Change Password</div>
            <div className="mono text-xs text-[#A0A0A0] mb-4">Account: <span className="amber">{user?.username}</span></div>
            <label className="label-term mb-1 block">Current Password</label>
            <input className="input-term mb-3" type="password" value={curPwd} onChange={e=>setCurPwd(e.target.value)} autoFocus data-testid="change-pwd-current" />
            <label className="label-term mb-1 block">New Password</label>
            <input className="input-term" type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submitChangePassword()} placeholder="≥8 chars, 1 uppercase, 1 number" data-testid="change-pwd-new" />
            <div className="flex gap-2 justify-end mt-4">
              <button className="btn-amber text-xs" onClick={()=>{ setPwdOpen(false); setCurPwd(""); setNewPwd(""); }} data-testid="change-pwd-cancel">Cancel</button>
              <button className="btn-amber text-xs" onClick={submitChangePassword} data-testid="change-pwd-submit">Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
